import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "../../api/api";
import { useAcademicYear } from "../../AcademicYearContext";

import {
  Card,
  Typography,
  Skeleton,
  Tooltip,
} from "antd";

import {
  TeamOutlined,
  ManOutlined,
  WomanOutlined,
  ReloadOutlined,
  ArrowUpOutlined,
} from "@ant-design/icons";

import { motion } from "framer-motion";

const { Text } = Typography;

const TotalStudentsCard = ({ theme }) => {
  const { selectedYear } = useAcademicYear();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const [stats, setStats] = useState({
    total_students: 0,
    total_male: 0,
    total_female: 0,
  });

  const fetchCount = useCallback(async () => {
    if (!selectedYear?.id) return;

    try {
      setLoading(true);
      setError(false);

      const res = await api.get(
        `/yearly-student-admission-count/?academic_year=${selectedYear.id}`
      );

      setStats({
        total_students: Number(res.data?.total_students || 0),
        total_male: Number(res.data?.total_male || 0),
        total_female: Number(res.data?.total_female || 0),
      });
    } catch (err) {
      console.error("Failed to fetch student count:", err);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [selectedYear?.id]);

  useEffect(() => {
    fetchCount();
  }, [fetchCount]);

  useEffect(() => {
    const handleAcademicYearUpdate = () => {
      fetchCount();
    };

    window.addEventListener(
      "academic-year-updated",
      handleAcademicYearUpdate
    );

    window.addEventListener(
      "academicYearUpdated",
      handleAcademicYearUpdate
    );

    window.addEventListener(
      "studentCountUpdated",
      handleAcademicYearUpdate
    );

    return () => {
      window.removeEventListener(
        "academic-year-updated",
        handleAcademicYearUpdate
      );

      window.removeEventListener(
        "academicYearUpdated",
        handleAcademicYearUpdate
      );

      window.removeEventListener(
        "studentCountUpdated",
        handleAcademicYearUpdate
      );
    };
  }, [fetchCount]);

  const malePercentage = useMemo(() => {
    if (!stats.total_students) return 0;
    return Math.round(
      (stats.total_male / stats.total_students) * 100
    );
  }, [stats.total_male, stats.total_students]);

  const femalePercentage = useMemo(() => {
    if (!stats.total_students) return 0;
    return Math.round(
      (stats.total_female / stats.total_students) * 100
    );
  }, [stats.total_female, stats.total_students]);

  if (loading) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        style={{ height: "100%" }}
      >
        <Card
          bordered={false}
          style={{
            height: "100%",
            minHeight: 218,
            borderRadius: 22,
            background: theme.card,
            border: `1px solid ${theme.border}`,
            boxShadow:
              "0 14px 40px rgba(6,42,75,.07)",
          }}
          styles={{
            body: {
              padding: 22,
            },
          }}
        >
          <Skeleton
            active
            paragraph={{ rows: 4 }}
          />
        </Card>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{
        y: -5,
        transition: { duration: 0.2 },
      }}
      style={{ height: "100%" }}
    >
      <Card
        bordered={false}
        style={{
          height: "100%",
          minHeight: 218,
          borderRadius: 22,
          overflow: "hidden",
          position: "relative",
          background: theme.card,
          border: `1px solid ${theme.border}`,
          boxShadow:
            "0 14px 40px rgba(6,42,75,.07)",
        }}
        styles={{
          body: {
            padding: 0,
            height: "100%",
          },
        }}
      >
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: 4,
            background:
              "linear-gradient(90deg,#062A4B,#0F6DAB,#C0750D)",
          }}
        />

        <div
          style={{
            padding: 22,
            height: "100%",
            boxSizing: "border-box",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              gap: 12,
            }}
          >
            <div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 9,
                  marginBottom: 8,
                }}
              >
                <div
                  style={{
                    width: 42,
                    height: 42,
                    borderRadius: 13,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background:
                      "linear-gradient(135deg,#062A4B,#0F6DAB)",
                    color: "#fff",
                    fontSize: 20,
                    boxShadow:
                      "0 7px 18px rgba(6,42,75,.20)",
                  }}
                >
                  <TeamOutlined />
                </div>

                <div>
                  <Text
                    style={{
                      display: "block",
                      color: theme.text,
                      fontWeight: 700,
                      fontSize: 14,
                    }}
                  >
                    Student Population
                  </Text>

                  <Text
                    style={{
                      color: theme.secondary,
                      fontSize: 11,
                    }}
                  >
                    {selectedYear?.name || "Academic Year"}
                  </Text>
                </div>
              </div>
            </div>

            <Tooltip title="Refresh">
              <button
                type="button"
                onClick={fetchCount}
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 10,
                  border: `1px solid ${theme.border}`,
                  background: theme.soft,
                  color: theme.secondary,
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <ReloadOutlined />
              </button>
            </Tooltip>
          </div>

          <div
            style={{
              marginTop: 16,
              display: "flex",
              alignItems: "baseline",
              gap: 8,
            }}
          >
            <span
              style={{
                fontSize: 38,
                lineHeight: 1,
                fontWeight: 800,
                letterSpacing: "-1.5px",
                color: theme.text,
              }}
            >
              {error ? "—" : stats.total_students.toLocaleString()}
            </span>

            <span
              style={{
                fontSize: 12,
                fontWeight: 700,
                color: "#4FAE22",
              }}
            >
              <ArrowUpOutlined /> Total
            </span>
          </div>

          <Text
            style={{
              display: "block",
              marginTop: 5,
              color: theme.secondary,
              fontSize: 12,
            }}
          >
            Enrolled students for the selected year or team
          </Text>

          {error ? (
            <button
              type="button"
              onClick={fetchCount}
              style={{
                marginTop: 14,
                border: 0,
                background: "transparent",
                padding: 0,
                color: "#C0750D",
                cursor: "pointer",
                fontWeight: 700,
              }}
            >
              Unable to load. Try again
            </button>
          ) : (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 10,
                marginTop: 18,
              }}
            >
              <div
                style={{
                  padding: "11px 12px",
                  borderRadius: 14,
                  background:
                    theme.mode === "dark"
                      ? "rgba(15,109,171,.12)"
                      : "#F3F8FC",
                  border: `1px solid ${
                    theme.mode === "dark"
                      ? "rgba(15,109,171,.25)"
                      : "#E2EDF4"
                  }`,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 7,
                  }}
                >
                  <ManOutlined
                    style={{
                      color: "#0F6DAB",
                    }}
                  />

                  <Text
                    style={{
                      color: theme.secondary,
                      fontSize: 11,
                    }}
                  >
                    Male
                  </Text>
                </div>

                <div
                  style={{
                    marginTop: 4,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "baseline",
                  }}
                >
                  <strong
                    style={{
                      color: theme.text,
                      fontSize: 18,
                    }}
                  >
                    {stats.total_male.toLocaleString()}
                  </strong>

                  <span
                    style={{
                      color: theme.secondary,
                      fontSize: 10,
                    }}
                  >
                    {malePercentage}%
                  </span>
                </div>
              </div>

              <div
                style={{
                  padding: "11px 12px",
                  borderRadius: 14,
                  background:
                    theme.mode === "dark"
                      ? "rgba(192,117,13,.12)"
                      : "#FCF8F1",
                  border: `1px solid ${
                    theme.mode === "dark"
                      ? "rgba(192,117,13,.25)"
                      : "#F0E4CF"
                  }`,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 7,
                  }}
                >
                  <WomanOutlined
                    style={{
                      color: "#C0750D",
                    }}
                  />

                  <Text
                    style={{
                      color: theme.secondary,
                      fontSize: 11,
                    }}
                  >
                    Female
                  </Text>
                </div>

                <div
                  style={{
                    marginTop: 4,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "baseline",
                  }}
                >
                  <strong
                    style={{
                      color: theme.text,
                      fontSize: 18,
                    }}
                  >
                    {stats.total_female.toLocaleString()}
                  </strong>

                  <span
                    style={{
                      color: theme.secondary,
                      fontSize: 10,
                    }}
                  >
                    {femalePercentage}%
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      </Card>
    </motion.div>
  );
};

export default TotalStudentsCard;